import {guid} from "@core/Util/guid";
import {brickDefinitions} from "./brick-definitions";
import brickTypes from './brick-types';
import brickInitializers from './brick-initializers';
import dateHelpers from "@modules/JourneyBuilder/Util/date-helpers";
import {optionsCheckpoint, checkpointDefinitionsArray} from "./Checkpoints";

const ALL_CHECKPOINT_NAMES = checkpointDefinitionsArray.map(checkpoint => checkpoint.name);

const deepClone = (obj) => {
    //Null, undefined, etc. can't be serialized
    //thus we can simply return the original value
    if(!obj){
        return obj;
    }
    
    //Hacky, but works
    return JSON.parse(JSON.stringify(obj));
};

const createJourneyBrick = (brickDefinition, config) => {
    return createJourneyBrickFromModel({
        id: guid(),
        name: brickDefinition.displayName,
        type: brickDefinition.type,
        config: JSON.stringify(config || {}),
        isNew: true
    });
};

/**
 * Create a new journey brick implementation based on the given model
 * @param {Object} remoteModel
 * @return {JourneyBrick} New JourneyBrick implementation based on the given model
 */
const createJourneyBrickFromModel = (remoteModel) => {
    /**  @type {JourneyBrick} */
    const brick = {
        id: remoteModel.id,
        name: remoteModel.name,
        type: remoteModel.type,
        config: JSON.parse(remoteModel.config),
        connections: [],
        x: remoteModel.x || 0,
        y: remoteModel.y || 0,
        isNew: remoteModel.isNew || false,
        
        get isChildJourneyBrick(){
            return this.type === brickTypes.journey && this.config.isChildJourney === true;
        },
        get isParentJourneyBrick(){
            return this.type === brickTypes.journey && this.config.isChildJourney !== true;
        },
        get outgoingConnections() {
            return this.connections.filter(r => r.sourceBrickId === this.id);
        },
        get incomingConnections() {
            return this.connections.filter(r => r.destinationBrickId === this.id);
        },
        isActive(){
            //https://datanext.atlassian.net/browse/DPN-3314
            //Bricks are always displayed as active
            return true;
            
            //A brick is active when:
            // * It is the start of a path, or it belongs to an active path
            
            // if(this.isStart()) {
            //     return true;
            // }
            //
            // return this.incomingConnections
            //     .some(connection => connection.isActive());
        },
        isStart(){
            //A brick is the start of a path when:
            // * It is of type audience/journey AND has no incoming connections
            
            if(this.type === brickTypes.audience || this.type === brickTypes.journey){
                return this.incomingConnections.length === 0;
            }

            return false;
        },
        deregisterConnection(connection){
            this.connections.splice(
                this.connections.indexOf(connection), 
                1
            );
        },
        registerConnection(connection){
            if(this.connections.indexOf(connection) === -1){
                this.connections.push(connection);
            }
        },
        addConnection(options){
            //Optionally allow providing an id
            const id = options && options.id || guid();
            
            return createJourneyBrickConnectionFromModel({
                id: id
            }, this, true);
        }
    };

    //We should only set outgoing connections on initialize
    (remoteModel.connections || [])
        .filter(cm => cm.sourceBrickId === brick.id)
        .forEach(cm => createJourneyBrickConnectionFromModel(cm, brick, false));

    //Certain brick types require special actions/events, those will be executed/set in our initializer
    const initializer = brickInitializers[brick.type];
    if(initializer){
        initializer(brick);
    }
    
    return brick;
};

/**
 * Create a connection for the given brick based on the given model
 * @param {Object} remoteModel
 * @param {JourneyBrick} sourceBrick Brick to attach connection to
 * @param {boolean} isNew Indicates whether the connection was newly added, or simply created from an existing model
 * @return {JourneyBrickConnection} Newly created connection
 */
const createJourneyBrickConnectionFromModel = (remoteModel, sourceBrick, isNew) => {
    const config = remoteModel.config ? JSON.parse(remoteModel.config) : {};
    config[optionsCheckpoint.name] = {};

    let connectionDestinationBrick = undefined;

    /**  @type {JourneyBrickConnection} */
    const connection = {
        id: remoteModel.id,
        name: remoteModel.name,

        conditions: remoteModel.conditions || [],

        connectorX: remoteModel.connectorX || 0,
        connectorY: remoteModel.connectorY || 0,

        isDisabled: remoteModel.isDisabled || false,
        startDate: remoteModel.startDate || null,

        config: config,
        
        isActive(){
            return this._isActiveImpl(new Date());
        },
        // isPartOfActivePath(){
        //     return this._isPartOfActivePathImpl(new Set(), new Date());
        // },
        delete(){
            const { sourceBrick, destinationBrick } = this;
            
            //deregisterConnection should be called on both bricks
            //this to ensure any custom handlers get triggered
            sourceBrick.deregisterConnection(this);
            if(destinationBrick) destinationBrick.deregisterConnection(this);
        },
        _isActiveImpl(today){
            //A connection is displayed as active when it:
            // * is not explicitly disabled
            // * has either no startDate, or a startDate in the past/of today
            
            //https://datanext.atlassian.net/browse/DPN-3314
            //A connection being part of an active path no longer influences whether or not a connection is considered active
            
            if(this.isDisabled){
                return false;
            }

            if(this.startDate){
                const connectionHasReachedStartDate = dateHelpers
                    .getMomentFromString(connection.startDate)
                    .isSameOrBefore(today, 'day');
                
                if(!connectionHasReachedStartDate){
                    return false;
                }
            }
            
            // if(this.sourceBrick.isStart()) {
            //     return true;
            // }
            
            return true;
            
            // return this.sourceBrick.incomingConnections
            //     .some(connection => connection._isActiveImpl(seenConnections, today));
        },
        // _isPartOfActivePathImpl(seenConnections, today) {
        //     //A connection is considered part of an active path when:
        //     // * The connection itself is active
        //     // * its source is the start of a path, or it belongs to an active path
        //    
        //     //Protect against circular graphs
        //     //We can only achieve loopback when we ourselves have already been processed and determined as 'not directly active'
        //     if(seenConnections.has(this.id)){
        //         return false;
        //     }
        //
        //     seenConnections.add(this.id);
        //    
        //     if(!this._isActiveImpl(today)) {
        //         return false;
        //     }
        //
        //     if(this.sourceBrick.isStart()) {
        //         return true;
        //     }
        //    
        //     return this.sourceBrick.incomingConnections
        //       .some(connection => connection._isPartOfActivePathImpl(seenConnections, today));
        // }
    };

    if(isNew){
        //Default startDate to today
        connection.startDate = dateHelpers.getStringifiedDate(new Date());
    }

    //https://stackoverflow.com/a/34890593
    //Setting enumerable to false for our Source/Target bricks
    //prevents them from showing up in enumerations; and
    //thus serialization
    Object.defineProperties(connection, {
        //Purely local
        ui: {
            enumerable: false,
            writable: true,
            value: {
                borderColor: undefined,
                backgroundColor: undefined,
                cssClass: undefined,
                tooltip: {
                    title: undefined,
                    content: undefined
                }
            }
        },
        
        isConnected: {
            enumerable: false,
            get(){
                //Having both a source/destination brick should be enough for indicating we are 'connected'
                return !!this.sourceBrick && !!this.destinationBrick;
            }
        },
        
        isNew: {
            enumerable: false,
            writable: true,
            value: isNew
        },
        
        supportedCheckpoints: {
            enumerable: false,
            writable: true,
            value: [...ALL_CHECKPOINT_NAMES]
        },
        
        //Source is a static value, and will not change
        //thus we can simply make our source non-writeable
        sourceBrick: {
            enumerable: false,
            writable: false,
            value: sourceBrick
        },
        sourceBrickId: {
            enumerable: true,
            writable: false,
            value: sourceBrick.id
        },
        
        //Destination is not guaranteed to be available upon connection creation
        destinationBrick: {
            configurable: true, //Needs to be explicitly set to allow overwriting
            enumerable: false,
            set(value) {
                //Remove connection from previous brick
                if(connectionDestinationBrick && (!value || connectionDestinationBrick.id !== value.id)){
                    connectionDestinationBrick.deregisterConnection(this);
                } else if (isNew) {
                    // First connection, choose position for the checkpoints
                    this.connectorX = (value.x + this.sourceBrick.x + 85) / 2;
                    this.connectorY = (value.y + this.sourceBrick.y + 85) / 2;
                    
                    //https://datanext.atlassian.net/browse/DPN-3123
                    //Journey brick (parent) connections do not support an actual configuration, attempt to detect and clean unwanted defaults
                    if(value && value.isParentJourneyBrick) {
                        this.startDate = null;
                    }
                }
                
                //Add connection to new brick
                if(value && (!connectionDestinationBrick || connectionDestinationBrick.id !== value.id)){
                    value.registerConnection(this);
                }

                connectionDestinationBrick = value;
            },
            get: () => connectionDestinationBrick
        },
        destinationBrickId: {
            enumerable: true,
            get: () => connectionDestinationBrick && connectionDestinationBrick.id
        }
    });
    
    sourceBrick.registerConnection(connection);
    
    //'Fix' legacy connections by auto enabling all checkpoints by default
    //Disabled for intermediate deployment
    // if(isLegacyCheckpointlessConnection){
    //     checkpointDefinitionsArray
    //         .forEach(checkpoint => checkpoint.activateForConnection(connection));
    // }
    
    return connection;
};


const createFactory = (typeConfigMapping) => {
    //Type configs are remote defined
    return {
        createNewJourneyBrick: (type) => {
            const config = typeConfigMapping[type];

            return createJourneyBrick(
                brickDefinitions[type],
                deepClone(config)
            );      
        },
        createJourneyBrickFromModel: (remoteModel) => {
            return createJourneyBrickFromModel(remoteModel);
        }
    };
};

export default createFactory;